import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@attendance/ui/utils';
import { CalendarDays, CircleUser, Clock, Home, Stamp } from 'lucide-react';
import { useFeatureEnabled } from '../../stores/brandingStore.js';
import { useAuthStore } from '../../stores/authStore.js';
import { usePunchIntentStore } from '../../stores/punchIntentStore.js';

/**
 * THE RAIL, LYING DOWN — mobile navigation as a floating ink capsule.
 *
 * It used to be a full-bleed brand-coloured bar welded to the bottom edge. It
 * is now the same object the desktop rail is, turned on its side: an ink
 * capsule floating above the page, drawn from `--nav-surface` and the
 * `nav-on-surface` ink ramp, so the two navigations are one design at two
 * orientations rather than two designs that happen to be in one app. Every
 * colour here is a rail token; the brand-fill bar it replaces needed two
 * ink-at-an-alpha allowlist entries, and both go with it.
 *
 * ── THE ACTIVE TAB IS LIFTED OUT OF THE CAPSULE ────────────────────────────
 *
 * A white disc that rises above the ink and carries the icon AND the name.
 * Everything else is a glyph. That is the settled answer to a question this bar
 * has been through twice: full captions cost a third of the bar's height to
 * label four things a worker taps daily, and no captions at all leave a
 * bilingual workforce guessing at five glyphs. Labelling only WHERE YOU ARE
 * costs one word, and it is the word a reader most wants — the others are
 * reachable by tapping, but "which of these am I on" is not.
 *
 * ── AND THE PUNCH IS A SEPARATE DISC ───────────────────────────────────────
 *
 * Detached from the capsule, at the trailing end. It is not a destination and
 * it does not belong in a group of them: on the home screen it PERFORMS the
 * punch (see `punchIntentStore`), which is the one control on this bar that
 * changes something rather than going somewhere. Its own shape says so before
 * its glyph does.
 *
 * ── WHO GETS IT ────────────────────────────────────────────────────────────
 *
 * Every slot needs an Employee record behind the login, so a management-only
 * console renders NOTHING here — and `AppShell` does not mount it outside the
 * worker's workspace at all (`shellSurface.ts`). Hidden at md+, where the rail
 * itself is on screen.
 */

/** The one focus treatment, in the rail's own ink: `--ring` is `--primary`, and
 *  a brand ring on an ink capsule is nearly invisible. Canon geometry (2px at a
 *  2px offset, per packages/ui/src/lib/focus.ts), rail hue. */
const CAPSULE_FOCUS =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-on-surface';

export function MobileBottomNav(): JSX.Element | null {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const requestPunch = usePunchIntentStore((s) => s.request);
  const leaveOn = useFeatureEnabled('leave');
  const hasEmployeeRecord = useAuthStore((s) => Boolean(s.user?.employeeId));
  const selfService = useFeatureEnabled('selfService') && hasEmployeeRecord;
  /** `end`-style match: only the home screen itself, never a child route. */
  const onHome = pathname === '/';

  if (!selfService) return null;

  const tabs = [
    { to: '/', end: true, icon: Home, label: t('nav.home', 'Home') },
    { to: '/my-attendance', icon: Clock, label: t('mobileNav.attendance', 'Attendance') },
    ...(leaveOn
      ? [{ to: '/leave', icon: CalendarDays, label: t('mobileNav.leave', 'Leave') }]
      : []),
    { to: '/profile', icon: CircleUser, label: t('mobileNav.profile', 'Profile') },
  ];

  const punchLabel = t('nav.punch', 'Check in / out');

  return (
    /* `items-end` so the capsule and the punch disc share a floor while the
       active tab rises out of the capsule without moving either. */
    /* `bottom-4` FLOATS IT; the safe-area padding sits UNDER that.
       
       Both used to be padding-bottom on this element — `pb-4 pb-[env(...)]` —
       and the second wins, so on every browser that resolves the inset to 0
       (which is most of them) the capsule was welded to the bottom edge with
       no gap at all. Two declarations of one property is not two rules. The
       offset is a position now and the inset is the only padding. */
    <div className="fixed inset-x-0 bottom-4 z-30 flex items-end justify-center gap-element px-gutter-sm pb-[env(safe-area-inset-bottom)] md:hidden">
      <nav
        aria-label={t('mobileNav.label', 'Primary')}
        className="flex items-center gap-tight rounded-full bg-nav-surface p-1.5 shadow-popover"
      >
        {tabs.map(({ to, end, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            {...(end ? { end: true } : {})}
            aria-label={label}
            className={({ isActive }) =>
              cn(
                'flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full transition-colors duration-fast',
                CAPSULE_FOCUS,
                isActive
                  ? /* Lifted out of the ink, and the only slot that says its
                       name. `-mt-6` rather than a transform so the capsule's
                       own height is unchanged and the disc cannot overlap the
                       page content above it. */
                    /* A LOZENGE, NOT A STRICT CIRCLE, and the width is why.
                       At a fixed 56px "Attendance" rendered as "Atten…" — a
                       label kept on purpose and then cut is worse than no
                       label, and renaming the destination to fit a shape is
                       the tail wagging the dog. `px-3` lets the disc grow to
                       its word; short ones ("Home", "Leave") still come out
                       round because `min-w-touch` is the floor. */
                    '-mt-6 h-14 flex-col gap-0.5 bg-surface px-3 text-foreground shadow-hover'
                  : 'text-nav-on-surface-muted hover:bg-nav-hover-surface hover:text-nav-on-surface'
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={isActive ? 18 : 22} aria-hidden />
                {isActive && (
                  /* `text-label` is the 12.5px/600 label step — the token for
                     naming a control, which is what this is. It is the ONE
                     caption on the bar. */
                  <span className="whitespace-nowrap text-label leading-none">
                    {label}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {onHome ? (
        /* ON THE HOME SCREEN IT PUNCHES. A button, not a link, because it
           performs rather than navigates — as a link to `/` it did nothing at
           all on the one screen its name belongs to. */
        <button
          type="button"
          onClick={requestPunch}
          aria-label={punchLabel}
          className={cn(
            'flex h-14 w-14 min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-popover transition-colors duration-fast hover:bg-primary-hover',
            CAPSULE_FOCUS
          )}
        >
          <Stamp size={22} aria-hidden />
        </button>
      ) : (
        /* Anywhere else it is still a way TO the punch, which is a navigation. */
        <NavLink
          to="/"
          end
          aria-label={punchLabel}
          className={cn(
            'flex h-14 w-14 min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-popover transition-colors duration-fast hover:bg-primary-hover',
            CAPSULE_FOCUS
          )}
        >
          <Stamp size={22} aria-hidden />
        </NavLink>
      )}
    </div>
  );
}
